/**
 * TipTap / ProseMirror helpers for chapter comment highlights (parity with web reader/editor).
 */

import { getMarkRange } from "@tiptap/core";
import type { Node as PMNode, Schema } from "@tiptap/pm/model";
import type { Editor } from "@tiptap/react";
import type { Chapter, ChapterComment } from "../../shared/models";
import { syncChapterCommentsAnchorsFromEditor as syncAnchorsFromEditor } from "./chapterCommentAnchorSync";
import { resolveCommentAnchorRange } from "./chapterCommentAnchorSync";

function dispatchRemoveMarksAtRange(
  editor: Editor,
  from: number,
  to: number,
  markNames: readonly string[]
): void {
  if (from >= to || from < 0) return;
  const { state } = editor;
  const docSize = state.doc.content.size;
  const safeFrom = Math.max(0, Math.min(from, docSize));
  const safeTo = Math.max(safeFrom, Math.min(to, docSize));
  if (safeFrom >= safeTo) return;
  let tr = state.tr;
  let changed = false;
  for (const name of markNames) {
    const markType = state.schema.marks[name];
    if (!markType) continue;
    const before = tr.steps.length;
    tr = tr.removeMark(safeFrom, safeTo, markType);
    if (tr.steps.length !== before) changed = true;
  }
  if (!changed) return;
  tr.setMeta("addToHistory", false);
  editor.view.dispatch(tr);
}

export function dispatchAddMarksAtRange(
  editor: Editor,
  from: number,
  to: number,
  marks: readonly { name: string; attrs?: Record<string, unknown> }[]
): void {
  if (from >= to || from < 0) return;
  const { state } = editor;
  const docSize = state.doc.content.size;
  const safeFrom = Math.max(0, Math.min(from, docSize));
  const safeTo = Math.max(safeFrom, Math.min(to, docSize));
  if (safeFrom >= safeTo) return;
  let tr = state.tr;
  let changed = false;
  for (const m of marks) {
    const markType = state.schema.marks[m.name];
    if (!markType) continue;
    const mark = markType.create(m.attrs ?? {});
    const before = tr.steps.length;
    tr = tr.addMark(safeFrom, safeTo, mark);
    if (tr.steps.length !== before) changed = true;
  }
  if (!changed) return;
  tr.setMeta("addToHistory", false);
  editor.view.dispatch(tr);
}

export function findTargetTextRangeInEditor(editor: Editor, targetText: string): { from: number; to: number } | null {
  if (!editor?.state?.doc || !targetText) return null;
  const { doc } = editor.state;
  let found: { from: number; to: number } | null = null;
  doc.descendants((node, pos) => {
    if (found || !node.isText) return;
    const t = node.text ?? "";
    const i = t.indexOf(targetText);
    if (i !== -1) {
      found = { from: pos + i, to: pos + i + targetText.length };
      return false;
    }
    const lower = t.toLowerCase();
    const j = lower.indexOf(targetText.toLowerCase());
    if (j !== -1) {
      found = { from: pos + j, to: pos + j + targetText.length };
      return false;
    }
  });
  return found;
}

/** Inclusive span covered by comment and/or chapter highlight marks for `commentId`. */
export function findChapterCommentAnnotationRange(doc: PMNode, commentId: string): { from: number; to: number } | null {
  let min: number | null = null;
  let max: number | null = null;
  doc.descendants((node, pos) => {
    if (!node.isText) return;
    const text = node.text ?? "";
    for (let o = 0; o < text.length; o++) {
      const abs = pos + o;
      const $ = doc.resolve(abs + 1);
      const hit = $.marks().some(
        (m) =>
          (m.type.name === "comment" && String(m.attrs.commentId) === commentId) ||
          (m.type.name === "highlight" && String(m.attrs.chapterCommentId) === commentId)
      );
      if (hit) {
        if (min === null || abs < min) min = abs;
        if (max === null || abs + 1 > max) max = abs + 1;
      }
    }
  });
  if (min === null || max === null || min >= max) return null;
  return { from: min, to: max };
}

export function overlayCommentOrHighlightRange(
  doc: PMNode,
  pos: number,
  schema: Schema
): { from: number; to: number } | null {
  const size = doc.content.size;
  if (pos < 0 || pos > size || size < 2) return null;
  const clamped = Math.max(1, Math.min(pos, size - 1));
  const $pos = doc.resolve(clamped);
  const comment = schema.marks.comment;
  const highlight = schema.marks.highlight;
  if (comment) {
    const r = getMarkRange($pos, comment);
    if (r && r.from < r.to) return { from: r.from, to: r.to };
  }
  if (highlight) {
    const r = getMarkRange($pos, highlight);
    if (r && r.from < r.to) return { from: r.from, to: r.to };
  }
  return null;
}

export function getChapterCommentIdAtOverlayPos(doc: PMNode, schema: Schema, pos: number): string | null {
  const range = overlayCommentOrHighlightRange(doc, pos, schema);
  if (!range) return null;
  const inner = Math.min(Math.max(range.from + 1, 1), doc.content.size - 1);
  const $ = doc.resolve(inner);
  if (schema.marks.comment) {
    for (const m of $.marks()) {
      if (m.type === schema.marks.comment && m.attrs.commentId) {
        return String(m.attrs.commentId);
      }
    }
  }
  if (schema.marks.highlight) {
    for (const m of $.marks()) {
      if (m.type === schema.marks.highlight && m.attrs.chapterCommentId) {
        return String(m.attrs.chapterCommentId);
      }
    }
  }
  return null;
}

export function stripChapterCommentOverlayMarksAtRange(editor: Editor, from: number, to: number): void {
  dispatchRemoveMarksAtRange(editor, from, to, ["comment", "highlight"]);
}

export function stripAllChapterCommentOverlayMarks(editor: Editor): void {
  const size = editor.state.doc.content.size;
  if (size <= 1) return;
  dispatchRemoveMarksAtRange(editor, 0, size, ["comment", "highlight"]);
}

function storedCommentRange(
  editor: Editor,
  comment: ChapterComment
): { from: number; to: number } | null {
  return resolveCommentAnchorRange(editor, comment);
}

function marksForComment(comment: ChapterComment): { name: string; attrs?: Record<string, unknown> }[] {
  const type = comment.type ?? "note";
  const highlightAttrs = { chapterCommentId: comment.id };
  if (type === "highlight") {
    return [{ name: "highlight", attrs: highlightAttrs }];
  }
  return [
    { name: "highlight", attrs: highlightAttrs },
    { name: "comment", attrs: { commentId: comment.id, commentType: type } },
  ];
}

export function syncChapterCommentsAnchorsFromEditor(
  editor: Editor,
  comments: Chapter["comments"]
): Chapter["comments"] {
  return syncAnchorsFromEditor(editor, comments);
}

export function applyCommentMarkToRange(
  editor: Editor,
  commentId: string,
  commentType: string,
  from: number,
  to: number
): boolean {
  if (!editor?.state?.doc || to <= from) return false;
  const comment: ChapterComment = {
    id: commentId,
    text: "",
    type: commentType,
    resolved: false,
    createdBy: "user",
    createdAt: "",
  };
  dispatchAddMarksAtRange(editor, from, to, marksForComment(comment));
  return true;
}

export function scrollAndFlashChapterComment(editor: Editor, from: number, to: number): void {
  const well = document.querySelector(".manuscript-surface") as HTMLElement | null;
  const prose = editor.view.dom as HTMLElement;
  const view = editor.view;
  const docSize = editor.state.doc.content.size;

  const clampPos = (p: number) => Math.min(Math.max(p, 0), docSize);
  const lo = clampPos(Math.min(from, to));
  const hi = clampPos(Math.max(from, to));
  const f = Math.min(lo, hi);
  const t = Math.max(lo, hi);

  const unionVerticalFromSamples = (): { unionTop: number; unionBottom: number } | null => {
    try {
      const span = t - f;
      const sampleCount = Math.min(24, Math.max(2, 2 + Math.ceil(span / 48)));
      let unionTop = Number.POSITIVE_INFINITY;
      let unionBottom = Number.NEGATIVE_INFINITY;
      for (let i = 0; i < sampleCount; i++) {
        const p = clampPos(f + Math.round((span * i) / (sampleCount - 1)));
        const c = view.coordsAtPos(p);
        unionTop = Math.min(unionTop, c.top);
        unionBottom = Math.max(unionBottom, c.bottom);
      }
      if (!Number.isFinite(unionTop) || !Number.isFinite(unionBottom) || unionBottom <= unionTop) return null;
      return { unionTop, unionBottom };
    } catch {
      return null;
    }
  };

  const applyScroll = (): void => {
    const union = unionVerticalFromSamples();
    if (!union) {
      try {
        prose.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
      } catch {
        /* ignore */
      }
      return;
    }
    const { unionTop, unionBottom } = union;
    const unionHeight = unionBottom - unionTop;

    if (well) {
      const containerRect = well.getBoundingClientRect();
      const viewHeight = containerRect.height;
      const padding = 10;
      let deltaY: number;
      if (unionHeight > viewHeight - padding * 2) {
        const targetTop = containerRect.top + padding;
        deltaY = unionTop - targetTop;
      } else {
        const unionCenterY = (unionTop + unionBottom) / 2;
        const viewportCenterY = containerRect.top + viewHeight / 2;
        deltaY = unionCenterY - viewportCenterY;
      }
      const maxScroll = Math.max(0, well.scrollHeight - well.clientHeight);
      const nextTop = Math.min(maxScroll, Math.max(0, well.scrollTop + deltaY));
      well.scrollTo({ top: nextTop, behavior: "smooth" });
      return;
    }

    try {
      prose.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
    } catch {
      /* ignore */
    }
  };

  requestAnimationFrame(() => {
    requestAnimationFrame(applyScroll);
  });

  const flashAt = Math.min(Math.max(Math.floor((f + t) / 2), 0), docSize);
  try {
    const dom = view.domAtPos(flashAt);
    const node = dom.node;
    const flashEl =
      node.nodeType === Node.TEXT_NODE ? (node.parentElement as HTMLElement | null) : (node as HTMLElement);
    flashEl?.classList.add("comment-flash");
    window.setTimeout(() => flashEl?.classList.remove("comment-flash"), 2000);
  } catch {
    /* ignore */
  }
}

export function removeCommentMarkFromEditorByCommentId(editor: Editor, commentId: string): void {
  if (!editor?.state?.doc) return;
  const range = findChapterCommentAnnotationRange(editor.state.doc, commentId);
  if (!range) return;
  stripChapterCommentOverlayMarksAtRange(editor, range.from, range.to);
}

function chapterCommentHighlightHasLegacyInlineColor(
  editor: Editor,
  commentId: string,
  from: number,
  to: number
): boolean {
  const highlight = editor.state.schema.marks.highlight;
  if (!highlight) return false;
  let found = false;
  editor.state.doc.nodesBetween(from, to, (node) => {
    if (!node.isText) return;
    for (const m of node.marks) {
      if (
        m.type === highlight &&
        String(m.attrs.chapterCommentId) === commentId &&
        m.attrs.color
      ) {
        found = true;
      }
    }
  });
  return found;
}

/**
 * Apply highlight + comment marks from stored anchors (parity with web `applyMarksFromComments`).
 * Uses transactions only — no selection moves during rebuild.
 */
export function applyMarksFromChapterComments(
  editor: Editor,
  comments: Chapter["comments"],
  appliedIds: Set<string>
): void {
  if (!editor?.state?.doc) return;
  for (const c of comments) {
    if (c.resolved) {
      appliedIds.delete(c.id);
      continue;
    }
    const stored = storedCommentRange(editor, c);
    if (!stored) continue;

    const doc = editor.state.doc;
    const inDoc = findChapterCommentAnnotationRange(doc, c.id);
    const matches = inDoc != null && inDoc.from === stored.from && inDoc.to === stored.to;
    const legacyInlineColor =
      matches &&
      chapterCommentHighlightHasLegacyInlineColor(editor, c.id, stored.from, stored.to);
    if (appliedIds.has(c.id) && matches && !legacyInlineColor) continue;

    if (inDoc) {
      stripChapterCommentOverlayMarksAtRange(editor, inDoc.from, inDoc.to);
    }
    appliedIds.delete(c.id);

    try {
      dispatchAddMarksAtRange(editor, stored.from, stored.to, marksForComment(c));
      appliedIds.add(c.id);
    } catch {
      /* ignore bad ranges */
    }
  }
}

/** Chapter switch / hydrate — strip overlays then apply from comment metadata. */
export function reapplyChapterCommentMarks(
  editor: Editor | null,
  comments: Chapter["comments"],
  appliedIds: Set<string>
): void {
  if (!editor?.state?.doc) return;
  stripAllChapterCommentOverlayMarks(editor);
  appliedIds.clear();
  applyMarksFromChapterComments(editor, comments, appliedIds);
}

export function measureCommentToolbarPosition(editor: Editor, commentId: string): { top: number; left: number } | null {
  const range = findChapterCommentAnnotationRange(editor.state.doc, commentId);
  if (!range || range.from >= range.to) return null;
  return measureCoordsForRange(editor, range.from, range.to);
}

/** Position a floating UI near a document range (selection composer). */
export function measureCoordsForRange(
  editor: Editor,
  from: number,
  to: number
): { top: number; left: number } | null {
  const view = editor.view;
  try {
    const s = view.coordsAtPos(from);
    const e = view.coordsAtPos(Math.max(from, to));
    const minTop = Math.min(s.top, e.top);
    const maxRight = Math.max(s.right, e.right);
    return { top: Math.max(4, minTop - 6), left: maxRight };
  } catch {
    return null;
  }
}

/** @deprecated Use findChapterCommentAnnotationRange */
export function findCommentMarkRangeInDoc(doc: PMNode, commentId: string): { from: number; to: number } | null {
  return findChapterCommentAnnotationRange(doc, commentId);
}
